/**
 * R0003 Training Names Service
 *
 * Fetches the employee names behind each day's training hours so the SPLH report
 * can list them under the day's note. Display-only: these names never feed a
 * calculation and never enter the store notes.
 *
 * This reads the same `trainingHours` report as the shared
 * `src/services/trainingHoursService.js` (which aggregates the names away for
 * another report). It is kept separate, and deliberately not merged into that
 * service, so this report cannot change behavior for its other consumer.
 *
 * @module trainingNamesService
 *
 * FUTURE-PROOFING NOTES (shared with trainingHoursService):
 * - Date matching: training is stored as weekOf + day-of-week name, not a date.
 *   TODO: Update when the training DB migrates to date-based storage.
 * - Store matching: matches by store shortName.
 *   TODO: Update when the training DB migrates to locationId-based storage.
 */

import { API } from 'aws-amplify'
import { getReport } from 'graphql/queries'
import * as logger from 'utils/logger'

/**
 * Adds one training record's employee name to the correct FOH/BOH bucket.
 * Records without a usable name, or with no positive hours, are ignored — a
 * zero-hour record means the employee was not actually in training that day.
 *
 * @param {Object} buckets - Accumulator: `{ foh: Map, boh: Map }` keyed by dedupe key
 * @param {Object} record - Training record `{ _id, name, type, hours }`
 * @returns {void}
 */
const addRecordName = (buckets, record) => {
  const name = typeof record?.name === 'string' ? record.name.trim() : ''
  const hours = Number(record?.hours) || 0

  if (!name || hours <= 0) {
    return
  }

  const bucket = record.type === 'FOH'
    ? buckets.foh
    : record.type === 'BOH'
      ? buckets.boh
      : null

  // Records with any other (or missing) type are not displayable under a
  // FOH/BOH heading, so they are skipped rather than guessed at.
  if (!bucket) {
    return
  }

  // Dedupe on the record id when present so the same person entered twice for a
  // day (e.g. two shifts) is listed once; fall back to the name itself, which is
  // free text, so compare case-insensitively.
  const dedupeKey = record._id || name.toLowerCase()

  if (!bucket.has(dedupeKey)) {
    bucket.set(dedupeKey, name)
  }
}

/**
 * Extracts training employee names from a raw `trainingHours` report payload,
 * grouped by store, day, and FOH/BOH. Names are de-duplicated and sorted
 * alphabetically so the rendered list is stable across refetches.
 *
 * A day key is only created when at least one name lands in it, so callers can
 * treat a missing key as "no training that day".
 *
 * @param {Array|null} trainingData - Raw rows: `[{ store, data: [{ day, hours: [record] }] }]`
 * @returns {Object} `{ [storeShortName]: { [dayName]: { foh: string[], boh: string[] } } }`
 *
 * @example
 * extractTrainingNames(raw)['First Colony']['Monday'];
 * // { foh: ['hsolo', 'lskywalker'], boh: ['dvader'] }
 */
export const extractTrainingNames = (trainingData) => {
  if (!trainingData || !Array.isArray(trainingData)) {
    return {}
  }

  const result = {}

  for (const storeData of trainingData) {
    const storeName = storeData?.store

    if (!storeName || !Array.isArray(storeData.data)) {
      continue
    }

    for (const dayData of storeData.data) {
      const dayName = dayData?.day

      if (!dayName || !Array.isArray(dayData.hours)) {
        continue
      }

      const buckets = { foh: new Map(), boh: new Map() }

      for (const record of dayData.hours) {
        addRecordName(buckets, record)
      }

      if (buckets.foh.size === 0 && buckets.boh.size === 0) {
        continue
      }

      const sortNames = (bucket) =>
        Array.from(bucket.values()).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        )

      if (!result[storeName]) {
        result[storeName] = {}
      }

      result[storeName][dayName] = {
        foh: sortNames(buckets.foh),
        boh: sortNames(buckets.boh)
      }
    }
  }

  return result
}

/**
 * Fetches training employee names for one week, grouped by store, day, and type.
 *
 * Failures are non-fatal by design: the SPLH report must still render its
 * training hours and notes when this lookup fails, so every error path resolves
 * to an empty map rather than throwing.
 *
 * @param {Object} params - Fetch parameters
 * @param {Object} params.user - Authenticated user `{ userName, client }`
 * @param {string} params.weekOf - Week start (Monday) in 'yyyy-MM-dd' format
 * @returns {Promise<Object>} `{ [store]: { [dayName]: { foh: string[], boh: string[] } } }`,
 *   or `{}` when the request fails or returns no training data
 *
 * @example
 * const names = await fetchTrainingNamesForWeek({
 *   user: { userName: 'jane', client: 'grm' },
 *   weekOf: '2026-08-17'
 * });
 * // names['First Colony']['Monday'] -> { foh: ['hsolo'], boh: [] }
 */
export const fetchTrainingNamesForWeek = async ({ user, weekOf }) => {
  if (!user?.userName || !user?.client || !weekOf) {
    logger.warn('Skipping training names fetch: missing user or week', {
      hasUserName: Boolean(user?.userName),
      hasClient: Boolean(user?.client),
      hasWeekOf: Boolean(weekOf)
    })
    return {}
  }

  try {
    const reportVariables = [
      {
        name: 'arguments',
        value: JSON.stringify({
          weekOf,
          stores: ['all']
        })
      }
    ]

    const response = await API.graphql({
      query: getReport,
      variables: {
        userName: user.userName,
        clientId: user.client,
        reportId: 'trainingHours',
        reportVariables
      },
      authMode: 'AMAZON_COGNITO_USER_POOLS'
    })

    const cellValue = response?.data?.getReport?.[0]?.data?.[0]?.rowCells?.[0]?.value

    if (!cellValue) {
      logger.debug('No training names data for week', { weekOf })
      return {}
    }

    const namesByStoreDay = extractTrainingNames(JSON.parse(cellValue))

    // Employee names are PII — log counts only, never the names themselves.
    logger.debug('Fetched training names for week', {
      weekOf,
      storeCount: Object.keys(namesByStoreDay).length
    })

    return namesByStoreDay
  } catch (error) {
    logger.error('Failed to fetch training names for week', {
      error: error.message,
      weekOf
    })
    return {}
  }
}
